import { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { 
  X, Send, Loader2, Sparkles, Check, Copy, Edit2, 
  Linkedin, Twitter, Facebook, Instagram, CheckCircle2
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import ReactQuill from 'react-quill';
import DOMPurify from 'dompurify';
import 'react-quill/dist/quill.snow.css';

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

interface SkillItem {
  name: string;
  experience: string | null;
  required: boolean;
  id: string | null;
  matched: boolean;
}

interface GeneratedContent {
  description: string;
  requirements: string;
  skills: {
    technical: SkillItem[];
    soft: SkillItem[];
  };
  socialPosts: {
    linkedin: string;
    twitter: string;
    facebook: string;
    instagram: string;
  };
}

interface JobContext {
  title: string;
  clientName?: string;
  jobType?: string;
  workMode?: string;
  location?: string;
  country?: string;
  city?: string;
  salaryMin?: string;
  salaryMax?: string;
  salaryCurrency?: string;
  existingSkills?: string[];
}

interface JDGeneratorChatbotProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  jobContext: JobContext;
  onApply: (content: GeneratedContent) => void;
}

const JDGeneratorChatbot = ({ open, onOpenChange, jobContext, onApply }: JDGeneratorChatbotProps) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedContent, setGeneratedContent] = useState<GeneratedContent | null>(null);
  const [activeTab, setActiveTab] = useState('chat');
  const [editingDescription, setEditingDescription] = useState(false);
  const [editingRequirements, setEditingRequirements] = useState(false);
  const [editedDescription, setEditedDescription] = useState('');
  const [editedRequirements, setEditedRequirements] = useState('');
  const [copiedPost, setCopiedPost] = useState<string | null>(null);
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();

  useEffect(() => {
    if (open && messages.length === 0) {
      const missingInfo = [];
      if (!jobContext.title) missingInfo.push('job title');
      if (!jobContext.clientName) missingInfo.push('client/company name');
      if (!jobContext.jobType) missingInfo.push('job type (full-time, contract, etc.)');
      
      let greeting = `Hi! I'm here to help you create a comprehensive job description for **${jobContext.title || 'your role'}**.`;
      
      if (missingInfo.length > 0) {
        greeting += `\n\nTo create a great JD, I'd like to know more about:\n${missingInfo.map(i => `• ${i}`).join('\n')}\n\nWhat would you like to share about this role?`;
      } else {
        greeting += `\n\nI see you have the basics filled in. Would you like to:\n• Tell me more about the role's responsibilities?\n• Describe the ideal candidate?\n• Share any specific requirements?\n\nOr just click **"Generate JD"** and I'll create one based on the current context!`;
      }
      
      greeting += `\n\n**Note:** I'll organize skills into three categories:\n• **Required Technical** (with years of experience)\n• **Good to Have Technical**\n• **Soft Skills**`;
      
      setMessages([{ role: 'assistant', content: greeting }]);
    }
  }, [open, jobContext]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setInput('');
    setIsLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke('generate-jd-comprehensive', {
        body: {
          action: 'chat',
          jobContext,
          messages: messages.slice(-10),
          userMessage
        }
      });

      if (error) throw error;

      setMessages(prev => [...prev, { role: 'assistant', content: data.message }]);
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again.'
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGenerate = async () => {
    setIsGenerating(true);
    setActiveTab('results');

    try {
      const { data, error } = await supabase.functions.invoke('generate-jd-comprehensive', {
        body: {
          action: 'generate',
          jobContext,
          messages: messages.slice(-10)
        }
      });

      if (error) throw error;

      setGeneratedContent(data);
      setEditedDescription(data.description);
      setEditedRequirements(data.requirements);
      
      toast({
        title: 'JD Generated!',
        description: 'Review and edit the content, then apply to your job posting.'
      });
    } catch (error) {
      console.error('Generation error:', error);
      toast({
        title: 'Generation failed',
        description: 'Could not generate JD. Please try again.',
        variant: 'destructive'
      });
      setActiveTab('chat');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCopyPost = (platform: string, text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedPost(platform);
    setTimeout(() => setCopiedPost(null), 2000);
    toast({ title: 'Copied to clipboard!' });
  };

  const handleApply = () => {
    if (!generatedContent) return;
    
    const finalContent: GeneratedContent = {
      ...generatedContent,
      description: editedDescription,
      requirements: editedRequirements
    };
    
    onApply(finalContent);
    onOpenChange(false);
    toast({
      title: 'Content Applied',
      description: 'The generated content has been added to your job form.'
    });
  };

  const renderMessage = (content: string) => {
    return content
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\n/g, '<br/>');
  };

  const quillModules = {
    toolbar: [
      ['bold', 'italic', 'underline'],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      ['clean']
    ],
  };

  // Separate skills into required, good to have, and soft
  const getRequiredTechnical = () => generatedContent?.skills.technical.filter(s => s.required) || [];
  const getGoodToHave = () => generatedContent?.skills.technical.filter(s => !s.required) || [];
  const getSoftSkills = () => generatedContent?.skills.soft || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl h-[85vh] flex flex-col p-0 gap-0">
        <DialogHeader className="px-6 py-4 border-b">
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            AI Job Description Generator
          </DialogTitle>
          <DialogDescription>
            Chat to refine requirements, then generate description, requirements, skills & social posts
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
          <TabsList className="mx-6 mt-2 w-fit">
            <TabsTrigger value="chat">Chat</TabsTrigger>
            <TabsTrigger value="results" disabled={!generatedContent}>Results</TabsTrigger>
            <TabsTrigger value="social" disabled={!generatedContent}>Social Posts</TabsTrigger>
          </TabsList>

          <TabsContent value="chat" className="flex-1 flex flex-col overflow-hidden m-0 p-0">
            {/* Context Summary */}
            <div className="px-6 py-3 bg-muted/50 border-b">
              <div className="flex flex-wrap gap-2 text-sm">
                {jobContext.title && <Badge variant="secondary">{jobContext.title}</Badge>}
                {jobContext.clientName && <Badge variant="outline">{jobContext.clientName}</Badge>}
                {jobContext.jobType && <Badge variant="outline">{jobContext.jobType.replace('_', ' ')}</Badge>}
                {jobContext.workMode && <Badge variant="outline">{jobContext.workMode}</Badge>}
                {(jobContext.city || jobContext.country) && (
                  <Badge variant="outline">{[jobContext.city, jobContext.country].filter(Boolean).join(', ')}</Badge>
                )}
              </div>
            </div>

            {/* Messages */}
            <ScrollArea className="flex-1 px-6 py-4">
              <div className="space-y-4">
                {messages.map((msg, idx) => (
                  <div
                    key={idx}
                    className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <Card
                      className={`max-w-[80%] p-3 ${
                        msg.role === 'user'
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted'
                      }`}
                    >
                      <div 
                        className="text-sm"
                        dangerouslySetInnerHTML={{ __html: renderMessage(msg.content) }}
                      />
                    </Card>
                  </div>
                ))}
                {isLoading && (
                  <div className="flex justify-start">
                    <Card className="p-3 bg-muted">
                      <Loader2 className="h-4 w-4 animate-spin" />
                    </Card>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            </ScrollArea>

            {/* Input */}
            <div className="px-6 py-4 border-t bg-background">
              <div className="flex gap-2">
                <Input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Tell me about the role, requirements, or ideal candidate..."
                  onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
                  disabled={isLoading}
                />
                <Button onClick={handleSend} disabled={isLoading || !input.trim()}>
                  <Send className="h-4 w-4" />
                </Button>
                <Button onClick={handleGenerate} disabled={isGenerating} variant="default">
                  {isGenerating ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Sparkles className="h-4 w-4 mr-2" />
                  )}
                  Generate JD
                </Button>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="results" className="flex-1 overflow-hidden m-0 p-0">
            <ScrollArea className="h-full">
              <div className="p-6 space-y-6">
                {generatedContent && (
                  <>
                    {/* Description */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label className="text-base font-semibold">Description</Label>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setEditingDescription(!editingDescription)}
                        >
                          <Edit2 className="h-4 w-4 mr-1" />
                          {editingDescription ? 'Preview' : 'Edit'}
                        </Button>
                      </div>
                      {editingDescription ? (
                        <ReactQuill
                          theme="snow"
                          value={editedDescription}
                          onChange={setEditedDescription}
                          modules={quillModules}
                          className="bg-background"
                        />
                      ) : (
                        <Card className="p-4">
                          <div 
                            className="prose prose-sm max-w-none"
                            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(editedDescription) }}
                          />
                        </Card>
                      )}
                    </div>

                    {/* Requirements */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label className="text-base font-semibold">Requirements</Label>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setEditingRequirements(!editingRequirements)}
                        >
                          <Edit2 className="h-4 w-4 mr-1" />
                          {editingRequirements ? 'Preview' : 'Edit'}
                        </Button>
                      </div>
                      {editingRequirements ? (
                        <ReactQuill
                          theme="snow"
                          value={editedRequirements}
                          onChange={setEditedRequirements}
                          modules={quillModules}
                          className="bg-background"
                        />
                      ) : (
                        <Card className="p-4">
                          <div 
                            className="prose prose-sm max-w-none"
                            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(editedRequirements) }}
                          />
                        </Card>
                      )}
                    </div>

                    {/* Skills - 3 Sections */}
                    <div className="space-y-4">
                      <Label className="text-base font-semibold">Skills</Label>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* Required Technical Skills */}
                        <Card className="p-4">
                          <h4 className="font-medium mb-3 text-sm text-primary">Required Technical</h4>
                          <div className="space-y-2">
                            {getRequiredTechnical().map((skill, idx) => (
                              <div key={idx} className="flex items-center justify-between text-sm">
                                <div className="flex items-center gap-2">
                                  {skill.matched ? (
                                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                                  ) : (
                                    <div className="h-4 w-4 rounded-full border border-muted-foreground" />
                                  )}
                                  <span>{skill.name}</span>
                                </div>
                                {skill.experience && (
                                  <span className="text-muted-foreground text-xs">{skill.experience}</span>
                                )}
                              </div>
                            ))}
                            {getRequiredTechnical().length === 0 && (
                              <p className="text-xs text-muted-foreground">None identified</p>
                            )}
                          </div>
                        </Card>

                        {/* Good to Have Skills */}
                        <Card className="p-4">
                          <h4 className="font-medium mb-3 text-sm text-muted-foreground">Good to Have</h4>
                          <div className="space-y-2">
                            {getGoodToHave().map((skill, idx) => (
                              <div key={idx} className="flex items-center gap-2 text-sm">
                                {skill.matched ? (
                                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                                ) : (
                                  <div className="h-4 w-4 rounded-full border border-muted-foreground" />
                                )}
                                <span>{skill.name}</span>
                              </div>
                            ))}
                            {getGoodToHave().length === 0 && (
                              <p className="text-xs text-muted-foreground">None identified</p>
                            )}
                          </div>
                        </Card>

                        {/* Soft Skills */}
                        <Card className="p-4">
                          <h4 className="font-medium mb-3 text-sm text-muted-foreground">Soft Skills</h4>
                          <div className="space-y-2">
                            {getSoftSkills().map((skill, idx) => (
                              <div key={idx} className="flex items-center gap-2 text-sm">
                                {skill.matched ? (
                                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                                ) : (
                                  <div className="h-4 w-4 rounded-full border border-muted-foreground" />
                                )}
                                <span>{skill.name}</span>
                              </div>
                            ))}
                            {getSoftSkills().length === 0 && (
                              <p className="text-xs text-muted-foreground">None identified</p>
                            )}
                          </div>
                        </Card>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        <CheckCircle2 className="h-3 w-3 inline mr-1 text-green-500" />
                        Skills with checkmarks are matched to your database and will be auto-linked.
                      </p>
                    </div>

                    {/* Apply Button */}
                    <div className="flex justify-end pt-4 border-t">
                      <Button onClick={handleApply} size="lg">
                        <Check className="h-4 w-4 mr-2" />
                        Apply to Job Form
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </ScrollArea>
          </TabsContent>

          <TabsContent value="social" className="flex-1 overflow-hidden m-0 p-0">
            <ScrollArea className="h-full">
              <div className="p-6 space-y-4">
                {generatedContent?.socialPosts && (
                  <>
                    {/* LinkedIn */}
                    <Card className="p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <Linkedin className="h-5 w-5 text-blue-600" />
                          <span className="font-medium">LinkedIn</span>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleCopyPost('linkedin', generatedContent.socialPosts.linkedin)}
                        >
                          {copiedPost === 'linkedin' ? (
                            <Check className="h-4 w-4 text-green-500" />
                          ) : (
                            <Copy className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                      <p className="text-sm whitespace-pre-wrap">{generatedContent.socialPosts.linkedin}</p>
                    </Card>

                    {/* Twitter */}
                    <Card className="p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <Twitter className="h-5 w-5" />
                          <span className="font-medium">Twitter / X</span>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleCopyPost('twitter', generatedContent.socialPosts.twitter)}
                        >
                          {copiedPost === 'twitter' ? (
                            <Check className="h-4 w-4 text-green-500" />
                          ) : (
                            <Copy className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                      <p className="text-sm whitespace-pre-wrap">{generatedContent.socialPosts.twitter}</p>
                    </Card>

                    {/* Facebook */}
                    <Card className="p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <Facebook className="h-5 w-5 text-blue-500" />
                          <span className="font-medium">Facebook</span>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleCopyPost('facebook', generatedContent.socialPosts.facebook)}
                        >
                          {copiedPost === 'facebook' ? (
                            <Check className="h-4 w-4 text-green-500" />
                          ) : (
                            <Copy className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                      <p className="text-sm whitespace-pre-wrap">{generatedContent.socialPosts.facebook}</p>
                    </Card>

                    {/* Instagram */}
                    <Card className="p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <Instagram className="h-5 w-5 text-pink-500" />
                          <span className="font-medium">Instagram</span>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleCopyPost('instagram', generatedContent.socialPosts.instagram)}
                        >
                          {copiedPost === 'instagram' ? (
                            <Check className="h-4 w-4 text-green-500" />
                          ) : (
                            <Copy className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                      <p className="text-sm whitespace-pre-wrap">{generatedContent.socialPosts.instagram}</p>
                    </Card>
                  </>
                )}
              </div>
            </ScrollArea>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default JDGeneratorChatbot;
